import { Injectable, computed, signal } from '@angular/core';
import { ThirdPartyIntegrationsService } from './third-party-integrations.service';

//
// Ports the plain jQuery UI-wiring in gmr-app.js that sits OUTSIDE the AngularJS controller
// (goBookmark(), the .goParam/.back/.functionsLogo click handlers, the settings modal, the
// mobile/app-launcher menu toggles, and Escape-to-close). These never went through $scope, so
// they become plain component state here rather than anything tied to the old digest cycle.
//
export type WizardStep = 'pickFunction' | 'parameterForm';

const isMobileOrAndroid = () => /Mobi/i.test(navigator.userAgent) || /Android/i.test(navigator.userAgent);

@Injectable({ providedIn: 'root' })
export class UiStateService
{
  constructor(private readonly thirdParty: ThirdPartyIntegrationsService) {}

  readonly step = signal<WizardStep>('pickFunction');
  readonly settingsOpen = signal(false);
  readonly mobileMenuOpen = signal(false);
  readonly appLauncherOpen = signal(false);
  readonly showMobileDownloadButton = signal(false);

  //
  // 1:1 port of goBookmark() + the .goParam click handler's tracking side-effects.
  //
  // The tracking half is no longer a port. The original raised a single vendor event here and
  // nothing else, so opening a report was invisible to Matomo - and since this application never
  // loads a second document, that meant the whole visit was invisible beyond the landing. It is now
  // a page view in its own right, which is what Behaviour, funnels and heatmap targeting all need,
  // plus an event that a goal can be defined on.
  //
  // The page title is built from the preset name rather than from the report's translated
  // description: the description is rendered in the user's own language, which would split one
  // report across four titles in the Page Titles report for no gain.
  //
  goToParameterForm(presetName: string): void
  {
    this.step.set('parameterForm');
    if (isMobileOrAndroid())
    {
      this.showMobileDownloadButton.set(true);
    }

    this.thirdParty.trackNavigation('/app/report/' + presetName, 'Report: ' + presetName);
    this.thirdParty.trackEvent('Report', 'Opened', presetName);
  }

  //
  // Port of the .back / .functionsLogo click handlers: show the picker AND drop the #preset from the
  // address bar, so the URL keeps describing what is on screen.
  //
  // Those handlers sit on <a href="/menu.html"> elements and, until 2026-08-13, did not cancel the
  // click. The state change happened and was then immediately thrown away by the browser following
  // the href: a full document load, re-bootstrapping Angular and re-fetching the whole catalog, just
  // to return to a screen the app already had in memory. That is the "going back to the menu takes
  // ages" complaint.
  //
  // The href stays, deliberately. It is what makes these real links: hover target, middle-click and
  // "open in new tab" all keep working, and without it the element is not keyboard-focusable.
  //
  goToFunctionPicker(event?: MouseEvent): void
  {
    //
    // Only take over an ordinary left click. A modified click (Ctrl/Cmd/Shift/Alt, or a non-primary
    // button) means the user asked the BROWSER to do something - open a tab, a window, a download -
    // so cancelling it would break an expectation the app has no business overriding. Middle clicks
    // do not arrive here at all: modern browsers fire auxclick for those, not click.
    //
    const isPlainLeftClick = !event
      || (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey);

    if (!isPlainLeftClick) return;

    event?.preventDefault();
    this.showFunctionPicker();
    history.replaceState(null, '', ' ');
  }

  //
  // The same state change without touching the URL. Split out for history navigation: when the user
  // presses the browser's Back button the address bar has ALREADY been rewritten by the browser, and
  // writing to it again from the handler would fight the navigation that triggered us. replaceState
  // is what makes this split necessary rather than cosmetic - it silently rewrites the entry the user
  // just came back to.
  //
  showFunctionPicker(): void
  {
    //
    // Only a real transition is a navigation. This method also runs on the initial load with an empty
    // hash, and the tag-manager container has already reported that page view itself; reporting it
    // again here would count every entry to the application twice and put a phantom step in front of
    // every funnel.
    //
    const cameFromReport = this.step() === 'parameterForm';

    document.title = 'Get My Report';
    this.step.set('pickFunction');

    if (cameFromReport)
    {
      this.thirdParty.trackNavigation('/app/reports', 'Report overview');
    }
  }

  toggleSettings(): void
  {
    this.settingsOpen.update(open => !open);
  }

  closeSettings(): void
  {
    this.settingsOpen.set(false);
  }

  //
  // Mutually exclusive: opening one closes the other. Both panels share the same dimming overlay
  // and the same general screen area, so letting them stack open at once only produces an
  // ambiguous state ("which one does tapping outside close?") that no real design intends.
  //
  toggleMobileMenu(): void
  {
    this.mobileMenuOpen.update(open => !open);
    if (this.mobileMenuOpen())
    {
      this.appLauncherOpen.set(false);
    }
  }

  toggleAppLauncher(): void
  {
    this.appLauncherOpen.update(open => !open);
    if (this.appLauncherOpen())
    {
      this.mobileMenuOpen.set(false);
    }
  }

  readonly mobileOverlayVisible = computed(() => this.appLauncherOpen() || this.mobileMenuOpen());

  //
  // The original's own $('.mobileOverlay').on("click", ...) only ever toggled the app-launcher
  // menu, never the mobile menu - but that quirk was unreachable in practice, since the mobile
  // menu's own open-trigger (.mobileMenu) never matched anything and could never actually open (see
  // top-nav.component.html). Now that toggleMobileMenu() has a real, working trigger, and the two
  // panels are mutually exclusive (see above), closing both here is simple and always correct -
  // there's no real "original bug" left here to preserve.
  //
  handleMobileOverlayClick(): void
  {
    this.mobileMenuOpen.set(false);
    this.appLauncherOpen.set(false);
  }

  //
  // 1:1 port of document.onkeydown's Escape handler (settings modal only - the original never
  // wired Escape to the mobile/app-launcher menus).
  //
  handleEscapeKey(): void
  {
    this.settingsOpen.set(false);
  }
}
